import React, { useEffect, useState } from "react";
import { leaveApprovalApi, errorMessage } from "../adminApi";
import { useAdminToast } from "../components/Toast";
import { Card, Table, Badge, Button, Modal, Loading, ErrorState, PageHeader, DotsButton } from "../components/ui";
import { formatDate, leaveStatusColor } from "../format";

export default function LeaveApprovalPage() {
  const toast = useAdminToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await leaveApprovalApi.list());
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const doApprove = async (row) => {
    setBusy(true);
    try {
      await leaveApprovalApi.approve(row.id);
      toast.success("Cuti disetujui");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const doReject = async (row) => {
    const reason = window.prompt("Alasan menolak pengajuan cuti ini?");
    if (!reason) return;
    setBusy(true);
    try {
      await leaveApprovalApi.reject(row.id, { reason });
      toast.success("Cuti ditolak");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: "pemohon",
      header: "Pemohon",
      render: (r) => (
        <div>
          <div className="font-medium text-slate-800">{r.full_name || r.username}</div>
          <div className="text-[11px] text-slate-400">{r.nik || r.username}</div>
        </div>
      ),
    },
    { key: "type", header: "Jenis", render: (r) => r.leave_type_name || r.leave_type },
    {
      key: "range",
      header: "Tanggal",
      render: (r) => (
        <span className="whitespace-nowrap">{formatDate(r.date_from)} – {formatDate(r.date_to)}</span>
      ),
    },
    { key: "days", header: "Hari", render: (r) => <Badge color="blue">{r.days}</Badge> },
    { key: "status", header: "Status", render: (r) => <Badge color={leaveStatusColor(r.status)}>{r.status}</Badge> },
    {
      key: "act",
      header: "",
      render: (r) => <DotsButton onClick={() => setSelected(r)} />,
    },
  ];

  if (loading) return <Loading label="Memuat antrean cuti…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Approval Cuti"
        subtitle="Pengajuan cuti yang menunggu keputusan."
        action={<Button variant="secondary" size="sm" onClick={load}>↻ Refresh</Button>}
      />
      <Card className="flex-1 min-h-0 overflow-hidden">
        <Table columns={columns} rows={rows} empty="Tidak ada pengajuan cuti yang menunggu 🎉" />
      </Card>

      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        title="Detail Pengajuan Cuti"
        footer={
          selected && (
            <>
              <Button variant="danger" onClick={() => doReject(selected)} disabled={busy}>Tolak</Button>
              <Button variant="success" onClick={() => doApprove(selected)} disabled={busy}>Setujui</Button>
            </>
          )
        }
      >
        {selected && (
          <div className="space-y-3 text-sm">
            <Row label="Pemohon" value={`${selected.full_name || selected.username} (${selected.nik || "-"})`} />
            <Row label="Jenis" value={selected.leave_type_name || selected.leave_type} />
            <Row label="Tanggal" value={`${formatDate(selected.date_from)} – ${formatDate(selected.date_to)}`} />
            <Row label="Jumlah Hari" value={`${selected.days} hari`} />
            <Row label="Alasan" value={selected.reason || "-"} />
            {selected.document_url && (
              <div>
                <div className="text-[11px] text-slate-400 mb-1">Dokumen</div>
                <a href={selected.document_url} target="_blank" rel="noreferrer" className="text-brand-600 underline text-sm">
                  Lihat dokumen
                </a>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[11px] text-slate-400">{label}</div>
      <div className="text-slate-800">{value}</div>
    </div>
  );
}
